import { IBookVisitResult, ICustomerData } from '@app/shared/models';
import { Component, OnInit, OnDestroy, inject, Input } from '@angular/core';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { CustomerService } from '@app/shared/services/customer/customer.service';
import { ISeoTagType } from '@app/shared/services/seo/seo-tag-type.enum';
import { SEOService } from '@app/shared/services/seo/seo.service';
import { Observable, Subject, Subscription, of, tap, switchMap } from 'rxjs';
import { YesNoChoice } from '@app/shared/enums/yes-no-choice';
import { inOutAnimation, stopClickBubbling } from '@app/shared/utils';
import { CommonModule } from '@angular/common';
import { ControlContainer, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { BookingStepsFormType } from '@app/shared/models/booking-steps-form-type.model';
import { SharedModule } from '@app/shared/shared.module';
import { BookingPartChoosePaymentMethodComponent } from './choose-payment-method/choose-payment-method.component';
import { ISalonDetailsApi } from '@app/salon-details/models/salon-details-api.model';
import { BookingService } from '@app/shared/services/booking/booking.service';
import { ActivatedRoute, Router } from '@angular/router';
import { BookVisitErrorModalResult } from '@app/booking-part/models/book-visit-error-modal-result.enum';
import { BookVisitStatus } from '@app/shared/enums/book-visit-status.enum';
import { CustomerDataErrorModalComponent } from './customer-data-error-modal/customer-data-error-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { passwordRepeatValidator } from '@app/shared/validators/password-repeat/passwordRepeatValidator.directive';
import { BookingStepsFormService } from '@app/shared/services';
import { CreateVisitMapperService } from '@app/shared/services/booking/create-visit-mapper.service.';
import { CustomerDataFormSmartComponent } from './customer-data-form/customer-data-form.smart.component';
import { PaymentType } from '@app/shared/enums/payment-type.enum';
import { DownPaymentType } from '@app/shared/enums/down-payment-type.enum';

@Component({
  selector: 'app-booking-part-customer-data',
  templateUrl: './customer-data.smart.component.html',
  styleUrls: ['./customer-data.smart.component.scss'],
  animations: [inOutAnimation],
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    SharedModule,
    BookingPartChoosePaymentMethodComponent,
    CustomerDataFormSmartComponent],
  viewProviders: [{
    provide: ControlContainer,
    useExisting: FormGroupDirective
  }]
})
export class BookingPartCustomerDataSmartComponent implements OnInit, OnDestroy {
  @Input() salonDetails: ISalonDetailsApi;
  public isLoggedIn: boolean;
  public isAskForLoginState: boolean;
  public editMode = true;
  public shouldShowFormFields = true;
  public isOnlinePaymentAvailable: boolean;
  public isCustomerAdviceRequestEnabled: boolean;
  public isFormSubmitted = false;
  public onlineBookingTermsAndConditions = null;
  public YesNoChoice = YesNoChoice;
  public parentForm: BookingStepsFormType;
  public ngbModal = inject(NgbModal);
  public readonly parentFormDirective = inject(FormGroupDirective);
  private readonly bookingStepsFormService = inject(BookingStepsFormService);
  private readonly router = inject(Router);
  private readonly bookingService = inject(BookingService);
  private readonly route = inject(ActivatedRoute);
  private readonly createVisitMapperService = inject(CreateVisitMapperService);
  private readonly seoService = inject(SEOService);
  private readonly authService = inject(AuthService);
  private readonly customerService = inject(CustomerService);
  private masterSubscription = new Subscription();
  private isHiddenForOnlinePayments: boolean;

  constructor() {
    this.seoService.setSEO([
      {
        tagType: ISeoTagType.Title,
        i18nKey: 'BOOKING-PART.STEPS.CUSTOMER-DATA.META-TITLE'
      },
      {
        tagType: ISeoTagType.Description,
        i18nKey: 'BOOKING-PART.STEPS.CUSTOMER-DATA.META-DESCRIPTION'
      }
    ]);
  }

  @stopClickBubbling()
  setAskLoginState(stateActive: boolean): void {
    this.isAskForLoginState = stateActive;
  }

  ngOnInit(): void {
    this.parentForm = this.parentFormDirective.form;
    this.setupCustomerDataListener();
    this.pickBookingScenario();
    this.isCustomerAdviceRequestEnabled = this.salonDetails.allowsCustomerAdviceRequest;
    this.onlineBookingTermsAndConditions = this.salonDetails.onlineBookingTermsAndConditionsUrl;
    this.parentForm.controls.paymentType.valueChanges.subscribe();
    this.parentForm.controls.treatmentAssingments.controls.map(treatmentAssingment => treatmentAssingment.controls.isCollapsed.setValue(true));
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }

  public toggleEditMode(): void {
    this.editMode = !this.editMode;
    this.shouldShowFormFields = this.isLoggedIn ? !this.editMode : true;
  }

  public signIn(): void {
    this.authService.signIn$()
      .subscribe();
  }

  get countryPhoneCode() {
    return this.parentForm.controls.customerData.controls.country.value?.phoneCode;
  }

  public bookVisit(): void {
    if (this.parentForm.invalid) {
      this.isFormSubmitted = true;
    } else {
      const sub = this.bookingService.bookVisit(this.createVisitMapperService.createBookVisitModel(this.parentForm))
        .subscribe(
          (result: IBookVisitResult) => {
            this.dispatchActions(result);
          },
          () => {
            this.handleErrors(BookVisitStatus.Unknown);
          }
        );
      this.masterSubscription.add(sub);
    }
  }

  public get bookingForm(): BookingStepsFormType {
    return this.parentForm;
  }

  private handleErrors(bookVisitStatus: BookVisitStatus): void {
    const sub = this.showErrorModal$(bookVisitStatus)
      .subscribe(result => {
        switch (result) {
          case BookVisitErrorModalResult.ChangeVisitSlot:
            this.parentForm.controls.activeStep.setValue(0);
            break;
          case BookVisitErrorModalResult.TryAgain:
            this.bookVisit();
            break;
          default:
            break;
        }
      });

    this.masterSubscription.add(sub);
  }

  private setupCustomerDataListener(): void {
    const sub = this.authService.isLoggedIn$()
      .pipe(
        tap(isLoggedIn => {
          this.isLoggedIn = isLoggedIn;
          this.isAskForLoginState = !this.isLoggedIn;
          this.shouldShowFormFields = isLoggedIn ? !this.editMode : true;
          if (isLoggedIn) {
            this.parentForm.removeValidators(passwordRepeatValidator);
            this.parentForm.controls.password.clearValidators();
            this.parentForm.controls.passwordRepeat.clearValidators();
            this.parentForm.controls.salonRemarks.clearValidators();
            this.parentForm.controls.customerData.clearValidators();
            this.parentForm.controls.createAnAccount.setValue(true);
          }
        }),
        switchMap(isLoggedIn => {
          if (isLoggedIn) {
            return this.customerService.getCustomerData().pipe(
              tap((data) => this.parentForm.controls.customerData.patchValue(data))
            );
          }
          return of(null);
        }))
      .subscribe((result: ICustomerData | null) => {
        if (result === null) {
          this.bookingStepsFormService.buildCustomerDataFormGroup();
          return;
        }
        this.parentForm.controls.customerData.patchValue(result);
        this.parentForm.controls.createAnAccount.setValue(true);
      });
    this.masterSubscription.add(sub);
  }

  private showErrorModal$(status: BookVisitStatus): Observable<BookVisitErrorModalResult> {
    const subject = new Subject<BookVisitErrorModalResult>();
    const cmpRef = this.ngbModal.open(CustomerDataErrorModalComponent,
      {
        centered: true,
        backdrop: 'static'
      });
    (<CustomerDataErrorModalComponent>cmpRef.componentInstance).status = status;
    (<CustomerDataErrorModalComponent>cmpRef.componentInstance).subject = subject;
    return subject.asObservable();
  }

  private pickBookingScenario(): void {
    const selectedTreatments = this.parentForm.controls.treatmentAssingments.controls.map(x => (x.controls.treatment.value));
    this.isHiddenForOnlinePayments = selectedTreatments.every(
      item => item.mandatoryDownPayment === DownPaymentType.Disabled) &&
      selectedTreatments.some(item => item.isPriceVisibleForOnlineBooking === false);
    this.isOnlinePaymentAvailable =
      this.salonDetails.allowsOnlinePayments && !this.isHiddenForOnlinePayments;
    this.isOnlinePaymentAvailable ? this.parentForm.controls.paymentType.setValue(PaymentType.Online) : this.parentForm.controls.paymentType.setValue(PaymentType.InSalon);
  }

  private dispatchActions(result: IBookVisitResult): void {
    if (!this.parentForm.controls.createAnAccount.value) {
      if (result.authenticationToken) {
        this.authService.signInSilent$(result.authenticationToken)
          .subscribe();
      } else {
        // TODO: Show error that account wasn't created.
      }
    }
    switch (result.status) {
      case BookVisitStatus.Succeeded:
        if (this.parentForm.controls.paymentType.value === PaymentType.InSalon) {
          this.router.navigate(['..', result.visitId, 'booking-completed'], { relativeTo: this.route });
        } else {
          this.router.navigate(['..', result.visitId, 'payment-pending'], { queryParams: { paymentPageUrl: result.paymentPageUrl }, relativeTo: this.route });
        }
        break;
      default:
        this.handleErrors(result.status);
        break;
    }
  }
}
